// #9330 D1 (agreed by helix-ui-agent and helix-d9/helix-92, 2026-10-02): the executive reasoning
// stages a turn reports while it runs. Additive SSE event `turn.stage`, executive mode only;
// standard turns keep their exact bytes. A stage on the wire is a fact: the server emits it
// only after the phase it names has really begun or ended, never on a timer.

export const ASK_STAGE_SCHEMA_VERSION = 'ask-stage.v1' as const;

/** The six stages, in the only order they may be emitted. */
export const ASK_STAGE_NAMES = [
  'frame', 'break_down', 'hypothesize', 'test', 'challenge', 'advise',
] as const;
export type AskStageName = typeof ASK_STAGE_NAMES[number];

export const ASK_STAGE_STATUSES = ['started', 'completed', 'skipped'] as const;
export type AskStageStatus = typeof ASK_STAGE_STATUSES[number];

/**
 * Why a stage was skipped. There is no FAILED: a failed phase ends the turn as `turn.blocked`,
 * so "skipped" can never hide a failure.
 */
export const ASK_STAGE_SKIP_CODES = [
  'NOT_APPLICABLE', 'NO_FOCUS', 'NOT_READY', 'REFUSED',
  'TREE_UNAVAILABLE', 'FINDINGS_UNAVAILABLE', 'CAUSE_BRIDGE_UNAVAILABLE',
  'TREE_SNAPSHOT_MISMATCH', 'FOCUS_NOT_VISIBLE', 'SOURCE_PIN_MISMATCH', 'EXTENSION_DROPPED',
] as const;
export type AskStageSkipCode = typeof ASK_STAGE_SKIP_CODES[number];

/** A skipped stage always carries a typed reason with server-authored copy; others carry none. */
export type AskStageReason = Readonly<{ code: AskStageSkipCode; text: string }>;

export type AskStageOutcome =
  | Readonly<{ name: AskStageName; status: 'started' | 'completed'; reason: null }>
  | Readonly<{ name: AskStageName; status: 'skipped'; reason: AskStageReason }>;

/**
 * One `turn.stage` frame: `seq` strictly increases within a turn (gaps are allowed). Stages may
 * overlap: an open stage may finish after a later one starts. A stage is never started after a
 * later one was reached, never restarted, and never finished unless it was started.
 */
export type AskStageEventV1 = AskStageOutcome & Readonly<{
  schemaVersion: typeof ASK_STAGE_SCHEMA_VERSION;
  seq: number;
}>;

/** A stage's final state: only `completed` or `skipped` (never `started`). */
export type AskStageTerminalOutcome = Extract<AskStageOutcome, { status: 'skipped' }>
  | Readonly<{ name: AskStageName; status: 'completed'; reason: null }>;

/** `answer.completed` `response.stages`: each stage's terminal status, for replay (web: C2). */
export type AskStageSummaryV1 = readonly AskStageTerminalOutcome[];
